import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutateCreateOrUpdateCropMonitoring } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import type { EVENTS as CROP_MONITORING_EVENTS } from '/@/store/tracking/events/crop-monitoring';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import NewCropMonitoringFormModal from './components/NewCropMonitoringFormModal';
import ExitWithoutSavingModal from './ExitWithoutSavingModal';
import SuccessModal from './SuccessModal';
import type { CropMonitoringFormType, HybridFormType } from './types';
import { emptyHybrid, toFormHybrid } from './types';

export type { CropMonitoringFormType } from './types';
export { MAX_ENVIRONMENT, MIN_ENVIRONMENT } from './types';

const CropMonitoringDialog = ({
    open,
    setClose,
    field,
    entry_source,
    forceNew = false,
}: {
    open: boolean;
    setClose: () => void;
    field?: Field;
    entry_source: Parameters<(typeof CROP_MONITORING_EVENTS)['Add Crop Flow create began']>[0]['entry_source'];
    /** Always start a blank form, even if `field.activeCropMonitoring` is still set (e.g. a
     * campaign whose phenology already reached 'harvested' but has no explicit harvestDate yet). */
    forceNew?: boolean;
}) => {
    let cropMonitoring = field?.activeCropMonitoring;
    if (forceNew || cropMonitoring?.harvestDate) {
        cropMonitoring = undefined;
    }

    const onlyEdit = !!cropMonitoring?.id;

    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [showExitWarning, setShowExitWarning] = useState<boolean>(false);

    // Editing (CUL-1505, approach B): pre-fill one form block per active hybrid of the
    // campaign -- each hybrid is its own CropMonitoring.
    const activeCampaignCms = (field?.activeCropMonitorings ?? []).filter(
        (cm) => !cropMonitoring || cm.campaign === cropMonitoring.campaign
    );
    const defaultHybrids: HybridFormType[] = cropMonitoring
        ? activeCampaignCms.length
            ? activeCampaignCms.map(toFormHybrid)
            : [toFormHybrid(cropMonitoring)]
        : [emptyHybrid()];

    const form = useForm<CropMonitoringFormType>({
        defaultValues: {
            fieldId: cropMonitoring?.field || field?.id,
            id: cropMonitoring?.id,
            crop: cropMonitoring?.crop || '',
            previousCrop: cropMonitoring?.previousCrop || '',
            hybrids: defaultHybrids,
        },
    });

    useEffect(() => {
        if (!open || onlyEdit) return;
        mpTrackEvent('Add Crop Flow create began', { entry_source });
    }, [open, onlyEdit, entry_source]);

    const { fieldId, crop, previousCrop, hybrids } = form.watch();

    const { trigger: createOrUpdateCropMonitoring, isMutating } = useMutateCreateOrUpdateCropMonitoring(
        {
            hybrids,
            field: field?.id || fieldId || 0,
            crop: crop || undefined,
            previousCrop: previousCrop === 'none' ? undefined : previousCrop || undefined,
            id: cropMonitoring?.id,
        },
        {
            onSuccess: () => {
                setShowSuccess(true);
            },
        }
    );

    const handleClose = () => {
        if (isMutating) return;
        const hasData =
            crop ||
            previousCrop ||
            (hybrids || []).some((h) => h.plantingDate || h.product || h.technology || h.environment);
        if (hasData && !showSuccess) {
            setShowExitWarning(true);
        } else {
            setClose();
            form.reset();
        }
    };

    const handleDiscardAndExit = () => {
        setShowExitWarning(false);
        setClose();
        form.reset();
    };

    const handleSuccessClose = () => {
        setShowSuccess(false);
        setClose();
        form.reset();
    };

    if (!open) return null;

    return (
        <>
            <NewCropMonitoringFormModal
                open={open && !showSuccess && !showExitWarning}
                setClose={handleClose}
                form={form}
                field={field}
                onSubmit={() => createOrUpdateCropMonitoring()}
                submitting={isMutating}
                onlyEdit={onlyEdit}
                entry_source={entry_source}
            />
            <SuccessModal
                open={showSuccess}
                setClose={handleSuccessClose}
                crop={crop}
                fieldId={field?.id || fieldId || 0}
            />
            <ExitWithoutSavingModal
                open={showExitWarning}
                setClose={() => setShowExitWarning(false)}
                onDiscard={handleDiscardAndExit}
            />
        </>
    );
};

export default CropMonitoringDialog;
